import { logOperationalEvent } from 'saddlebag-logger';

const createFlushPromise = () => {
  let flushPromiseResolve;
  let flushPromiseReject;
  const flushPromise = new Promise((resolve, reject) => {
    flushPromiseResolve = resolve;
    flushPromiseReject = reject;
  });

  return { flushPromise, flushPromiseResolve, flushPromiseReject };
};

/**
 * Represents a buffer of serialized json messages which is flushed periodically
 * or when it reaches a certain length. The flushed messages are concatenated.
 * We calculate byte size by using TextEncoder on the message to get a close approximation of payload size.
 * This Class was created around the existing ProtobufMessagesBuffer found in node-grappler-sdk. Tweaked to handle same event types.
 */
class MessageBuffer {
  /**
   * Creates a new MessageBuffer.
   * @param {object} options - The options to configure the buffer.
   * @param {number} options.maxBufferSize - The maximum size of the buffer in bytes.
   * @param {boolean} options.flushOnExit - If true, the buffer will be flushed on page exit.
   * @param {number} options.flushAfter - The delay in milliseconds after which the buffer is flushed.
   * @param {function} options.flushCallback - The callback function to send the messages to the destination.
   * @param {object} options.callbackOpts - The options to be passed to the callback function.
   */
  constructor(options) {
    this.maxBufferSize = options.maxBufferSize;
    this.flushOnExit = options.flushOnExit;
    this.flushAfter = options.flushAfter;
    this.flushCallback = options.flushCallback;
    this.callbackOpts = options.callbackOpts;
    this.mechanism = options.callbackOpts.mechanism; // default mechanism to revert back to on returning user

    if (this.flushOnExit) {
      // on exit we should flush the buffer and attempt to send via beacon
      document.addEventListener(
        'visibilitychange',
        this.visibilityFlush.bind(this), // ensure callback has correct context
        {
          capture: true,
        },
      );
    }
    this.initBuffer();
  }

  /**
   * Flushes any pending messages when the page visibility changes to hidden.
   * Sets the mechanism to the default if the user returns after we overrode it for the flush.
   * @returns {void}
   */
  visibilityFlush() {
    if (document.visibilityState === 'hidden') {
      this.callbackOpts = { ...this.callbackOpts, mechanism: 'sendBeacon' };
      for (const url of this.messages.keys()) {
        this.flush(url);
      }
    } else {
      this.callbackOpts = { ...this.callbackOpts, mechanism: this.mechanism };
    }
  }

  /**
   * Adds a message to the buffer. If the buffer reaches the maximum size, it will be flushed.
   * @param {string} url - destination to send the message to
   * @param {object} message - message to be sent
   * @param {boolean} priority - flag to send the message immediately (if other messages are waiting those will be batched along with this event if url matches)
   * @returns {Promise} - promise indicating success or failure of the sent event.
   */
  addMessage(url, message, priority = false) {
    try {
      // to preserve resources we calulate the byte size of the message only once and pass it down.
      const messageLengthBytes = new TextEncoder().encode(message).length;
      const messages = this.messages.get(url);
      if (this.exceedsBufferSize(messages, messageLengthBytes)) {
        this.flush(url);
      }

      if (priority) {
        this.addMessageToBuffer(url, message, messageLengthBytes);
        const returnPromise = this.messages.get(url).flushPromise;
        this.flush(url);
        return returnPromise;
      }

      this.addMessageToBuffer(url, message, messageLengthBytes);

      return this.messages.get(url).flushPromise;
    } catch (error) {
      logOperationalEvent(
        'Could not add a message to the buffer. Reason: %s',
        error.message,
      );
      return Promise.reject(error);
    }
  }

  /**
   * Flushes the buffer of messages to the destination url.
   * @param {string} url - destination to send the messages to
   * @returns {Promise} - promise indicating success or failure of the sent messages.
   */
  flush(url) {
    const messages = this.messages.get(url);

    if ((messages && messages.buffer.length === 0) || messages === undefined) {
      return Promise.resolve();
    }

    const { flushPromiseReject, flushPromiseResolve } = messages;
    // wipes the buffer
    this.initBuffer(url);
    const bufferToFlush = messages.buffer.join('');
    const flushPayload = {
      url,
      buffer: bufferToFlush,
      options: this.callbackOpts,
    };

    this.stopFlushTimeout(url);
    return this.flushCallback(...Object.values(flushPayload))
      .then((res) => {
        flushPromiseResolve(res);
      })
      .catch((error) => {
        flushPromiseReject(error);
      });
  }

  /**
   * Stops the flush timeout for a given url. Necessary if flush is called manually or the message queue overflows.
   * @param {string} url - destination to send the messages to
   * @returns {void}
   */
  stopFlushTimeout(url) {
    const messages = this.messages.get(url);
    if (messages && messages.flushTimeout) {
      clearTimeout(messages.flushTimeout);
    }
  }

  /**
   * Starts the flush timeout for a given url. If the buffer is not flushed before the timeout, it will be flushed by the timeout after N Milliseconds specified by `this.flushAfter`.
   * @param {string} url - destination to send the messages to
   * @returns {void}
   */
  startFlushTimeout(url) {
    const messages = this.messages.get(url);
    if (messages) {
      this.stopFlushTimeout(url);
      messages.flushTimeout = setTimeout(
        () => this.flush(url),
        this.flushAfter,
      );
    }
  }

  /**
   * Adds a message to the buffer. If this is the first message in the queue, a new buffer is created.
   * @param {string} url - destination to send the message to
   * @param {string} message - a serialised message to be added to the buffer
   * @param {number} messageLengthBytes - length of the message in bytes
   * @returns {void}
   */
  addMessageToBuffer(url, message, messageLengthBytes) {
    const messages = this.messages.get(url);
    if (!messages) {
      this.messages.set(url, {
        buffer: [message],
        totalNumOfBytes: messageLengthBytes,
        ...createFlushPromise(),
      });
      this.startFlushTimeout(url);
    } else {
      messages.buffer.push(message);
      messages.totalNumOfBytes += messageLengthBytes;
    }
  }

  /**
   * Checks if the message exceeds the maximum buffer size.
   * @param {string[]} messages - array of serialised messages
   * @param {number} messageLengthBytes - message length in bytes
   * @returns {boolean} - true if the message exceeds the maximum buffer size
   * @throws {Error} - if the message is larger than the maximum buffer size
   */
  exceedsBufferSize(messages, messageLengthBytes) {
    if (messageLengthBytes > this.maxBufferSize) {
      throw new Error(
        `Message has size ${messageLengthBytes}B which is larger than ` +
          `the maximum buffer size (${this.maxBufferSize}B)`,
      );
    }
    const currentBufferLengthBytes = messages ? messages.totalNumOfBytes : 0;

    return currentBufferLengthBytes + messageLengthBytes > this.maxBufferSize;
  }

  /**
   * Initializes the buffer for a given url. If no url is provided, the buffer is initialized/wiped for all urls.
   * @param {string} url - destination to send the messages to
   * @returns {void}
   */
  initBuffer(url) {
    /**
     * Blocks of serialized data that will be concatenated once buffer is flushed.
     * @property {
     *   [url: string]: {
     *     buffer: string[];
     *     totalNumOfBytes: number;
     *     flushPromise: Promise;
     *     flushPromiseResolve: Promise.resolve;
     *     flushPromiseReject: Promies.reject;
     *     flushTimeout: Timeout
     *   };
     * } messages - The queue of messages to be sent to Slipstream grouped by url (same event types).
     */
    if (url) {
      this.messages.delete(url);
    } else {
      this.messages = new Map();
    }
  }
}

export { MessageBuffer };
